
import { supabase } from "@/integrations/supabase/client";
import { WordPair } from "@/types/game";

/**
 * Load game data from Supabase
 */
export const loadGameData = async (gameId: string | null) => {
  if (!gameId) {
    return { game: null, rounds: [], usedWords: new Set<string>(), currentRound: null, error: null };
  }

  try {
    // Check if game exists
    const { data: game, error: gameError } = await supabase
      .from("games")
      .select("*")
      .eq("id", gameId)
      .maybeSingle();
    
    if (gameError) throw gameError;
    
    if (!game) {
      return { 
        game: null, 
        rounds: [], 
        usedWords: new Set<string>(), 
        currentRound: null, 
        error: "Game not found" 
      };
    }
    
    // Load rounds
    const { data: roundsData, error: roundsError } = await supabase
      .from("rounds")
      .select("*")
      .eq("game_id", gameId)
      .order("round_number", { ascending: true });
      
    if (roundsError) throw roundsError;
    
    if (!roundsData) {
      return { 
        game,
        rounds: [], 
        usedWords: new Set<string>(), 
        currentRound: null, 
        error: null 
      };
    }
    
    const completedRounds = roundsData.filter(round => round.user_word && round.ai_word);
    const wordPairs = completedRounds.map(round => ({
      userWord: round.user_word || "",
      aiWord: round.ai_word || "",
      aiExplanation: round.ai_explanation || undefined,
      proximityScore: round.proximity_score
    }));
    
    // Set used words
    const usedWordsSet = new Set<string>();
    completedRounds.forEach(round => {
      if (round.user_word) usedWordsSet.add(round.user_word.toLowerCase());
      if (round.ai_word) usedWordsSet.add(round.ai_word.toLowerCase());
    });
    
    // Get the current round
    const currentRound = roundsData.find(round => round.round_number === completedRounds.length + 1);
    
    return {
      game,
      rounds: wordPairs,
      usedWords: usedWordsSet,
      currentRound,
      error: null
    };
  } catch (error) {
    console.error("Error loading game:", error);
    return { 
      game: null, 
      rounds: [], 
      usedWords: new Set<string>(), 
      currentRound: null, 
      error: "Error loading game data" 
    };
  }
};
